import { CertificationStatusChipOrIcon } from '@tableau/data-catalog-client-components';
import React from 'react';

import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/dataQuality/DataQualityType';
import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/catalog/models/IDataQualityIndicatorInfo';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { classes } from '~src/style';

import { Messages } from '../../../codegen/Localize';
import { IDataSourceMiniInfo } from '../../../datasource/models/IDataSourceInfo';
import { IUserInfo } from '../../../userManagement/users/models/IUserInfo';
import { ISpaceItemDetailInfo } from '../../models/ISpaceItemDetailInfo';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { HiddenUserStyles } from '../spaceComponents/SpacesStyles';
import { SpaceHeaderTestIds } from './SpaceHeader';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';

interface UserDisplayProps {
  displayName: string;
  url: string;
  displayAsText: boolean;
}

interface DatasourceDisplayProps {
  displayName: string;
  url: string;
}

function UserDisplay(props: UserDisplayProps) {
  if (props.displayAsText) {
    return (
      <span className={HiddenUserStyles.HiddenUserClassName}
        data-tb-test-id={SpaceHeaderTestIds.hiddenOwnerName}>
        {props.displayName}
      </span>);
  } else {
    return (
      <a className={SpaceHeaderStyles.headerLink}
        data-tb-test-id={SpaceHeaderTestIds.ownerName}
        href={props.url}>
        {props.displayName}
      </a>
    );
  }
}

function DatasourceDisplay(props: DatasourceDisplayProps) {
  return (
    <a className={SpaceHeaderStyles.headerLink}
      data-tb-test-id={SpaceHeaderTestIds.datasource}
      href={props.url}>
      {props.displayName}
    </a>
  );
}

export function OwnerMetadata(owner: IUserInfo, urls: SpacesSiteUrls): JSX.Element {
  return (owner && owner.displayName && (<React.Fragment>
    <span className={SpaceHeaderStyles.UserLabel}>
      {Messages.owner()}
    </span>
    <UserDisplay url={urls.userUrl(owner.username, owner.domainName)}
      displayAsText={owner.isHiddenInternalUser}
      displayName={owner.displayName} />
  </React.Fragment>));
}

export function DatasourceMetadata(datasourceInfo: IDataSourceMiniInfo, urls: SpacesSiteUrls): JSX.Element {
  return (datasourceInfo && datasourceInfo.name && (<React.Fragment>
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      {Messages.datasource()}
    </span>
    <DatasourceDisplay url={urls.datasourceUrl(datasourceInfo.id)}
      displayName={datasourceInfo.name} />
  </React.Fragment>));
}

export function ModifiedDateMetadata(modifiedAt: Date, withLeftPadding: boolean = true): JSX.Element {
  return <React.Fragment>
    <span className={withLeftPadding ? SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin : SpaceHeaderStyles.TextLabelWithNoLeftMargin}>
      {Messages.modified()}
    </span>
    <span className={SpaceHeaderStyles.MetadataInfo}
      data-tb-test-id={SpaceHeaderTestIds.modifiedAt}>
      {L10n.localizedDateTime(modifiedAt)}
    </span>
  </React.Fragment>;
}

export function ExtractStatusMetadata(hasExtracts: boolean, withLeftPadding: boolean = true): JSX.Element {
  const status = hasExtracts ? Messages.extract() : Messages.live();
  return <React.Fragment>
    <span className={withLeftPadding ? SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin : SpaceHeaderStyles.TextLabelWithNoLeftMargin}>
      {Messages.extractStatusLabel()}
    </span>
    <span className={SpaceHeaderStyles.MetadataInfo}
      data-tb-test-id={SpaceHeaderTestIds.modifiedAt}>
      {status}
    </span>
  </React.Fragment>;
}

export function PublishedConnectionPolicyMetaData(itemInfo: ISpaceItemDetailInfo<'virtualConnectionTable'>): JSX.Element {
  if (!itemInfo.hasDataPolicy) {
    return <></>;
  }
  return (
    <React.Fragment>
      <span className={classes(`tb-published-connection-policies-applied-metadata`, SpaceHeaderStyles.HeaderIcon)} />
      <span className={SpaceHeaderStyles.TextAndIconMetadataLabel}>
        {Messages.policiesApplied()}
      </span>
    </React.Fragment>
  );
}

export function CertificationMetadata(
  datasourceInfo: ISpaceItemDetailInfo<'datasource'>,
  urls: SpacesSiteUrls
): JSX.Element {
  const isCertified = datasourceInfo.certificationDetails && datasourceInfo.certificationDetails.isCertified;
  const indicators = datasourceInfo.dataQualityIndicators.filter(i => i.type === CERTIFIED_INDICATOR_TYPE);
  const indicator = indicators[0];

  return isCertified &&
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin} data-tb-test-id={SpaceHeaderTestIds.certifiedBy}>
      <CertificationStatusChipOrIcon
        subjectArea={'PublishedDatasource'}
        kind={'vizportal'}
        anchoredAsset={{
          assetName: datasourceInfo.name,
          assetType: 'PublishedDatasource',
          assetUrl: urls => urls.datasourceUrl(datasourceInfo.id)
        }}
        anchoredCertifications={[dataQualityIndicatorInfoToDataQualityIndicator(indicator)]}
        iconOnly={false}
        urls={urls}
      />
    </span>;
}

export const DefaultVerticalDividerPadding: number = 8;
export function VerticalDivider(rightPadding: number = DefaultVerticalDividerPadding, leftPadding: number = DefaultVerticalDividerPadding): JSX.Element {
  return <div style={{
    color: CommonStyles.Colors.Grey203,
    padding: `0px ${rightPadding}px 0px ${leftPadding}px`
  }}>|</div>
    ;
}


